/**
 * Full text and extraction contract (F16, milestone M4): documents and
 * uploads, full-text states, the extraction queue, the study review data and
 * value review actions. Re-exported from index.ts.
 *
 * Owner: fulltext and extraction (F16). Shapes follow
 * specs/features/F16-full-text-extraction/design.md, "HTTP contract
 * additions", narrowed to the first vertical slice (binary outcomes: arms and
 * per-arm events and totals).
 */
import type { Derivation, Span, ValueStatus } from '@bioterafi/evidence-model';
import { pagePath } from './paths';

export const EXTRACTION_ROUTES = {
  /** POST multipart {file, recordId, rightsConfirmed, role?} -> 201 / 200 DocumentResponse (E33; EXTR-020 to EXTR-025). */
  documents: (slug: string) => `${pagePath(slug)}/documents`,
  /** GET -> 200 DocumentResponse (E34; EXTR-140). DELETE -> 204, owner (E58; EXTR-027). */
  document: (slug: string, documentId: string) => `${pagePath(slug)}/documents/${encodeURIComponent(documentId)}`,
  /** GET -> 200 the stored bytes (E35; EXTR-036, EXTR-037). */
  documentFile: (slug: string, documentId: string) => `${pagePath(slug)}/documents/${encodeURIComponent(documentId)}/file`,
  /** GET ?page -> 200 DocumentTextResponse (E36; EXTR-141, EXTR-037). */
  documentText: (slug: string, documentId: string, page?: number) =>
    `${pagePath(slug)}/documents/${encodeURIComponent(documentId)}/text${page ? `?page=${page}` : ''}`,
  /** GET -> 200 FullTextListResponse: records included at title and abstract with their full-text state (EXTR-016, EXTR-026). */
  fullTexts: (slug: string) => `${pagePath(slug)}/full-texts`,
  /** GET -> 200 ExtractionQueueResponse (E60; EXTR-143). */
  extractionQueue: (slug: string) => `${pagePath(slug)}/extraction/queue`,
  /** GET -> 200 StudyExtractionResponse (E61; EXTR-144). */
  studyExtraction: (slug: string, studyId: string) => `${pagePath(slug)}/studies/${encodeURIComponent(studyId)}/extraction`,
  /** POST ValueReviewRequest -> 200 ValueReviewResponse (E38; EXTR-100 to EXTR-107). */
  valueReview: (slug: string) => `${pagePath(slug)}/values/review`,
} as const;

/** EXTR-022: the version of the rights text the upload dialog shows. */
export const RIGHTS_TEXT_VERSION = 'rights-v1';
/** EXTR-022, design "Rights confirmation text". White-label wording (CONT-058): no provider name. */
export const RIGHTS_TEXT =
  'I confirm that I have the right to store this document for this review, for example because I hold a licence or subscription that allows it or I am an author of it. Bioterafi will keep it privately in this workspace, will send its text to our AI service to extract data, and will delete it if this page or workspace is deleted.';

/** EXTR-023 as amended: PDF, or plain text for documents without a usable text layer. */
export const MAX_DOCUMENT_BYTES = 52_428_800;

export type DocumentOrigin = 'dataset' | 'europepmc' | 'unpaywall' | 'upload' | 'abstract' | 'registry' | 'reference_dataset';
export type DocumentRole = 'main_report' | 'supplement' | 'protocol' | 'statistical_analysis_plan' | 'other';
export type StoragePolicy = 'full' | 'spans_only';
export type LicenceCode =
  | 'cc-by'
  | 'cc-by-sa'
  | 'cc0'
  | 'public-domain'
  | 'cc-by-nc'
  | 'cc-by-nc-sa'
  | 'cc-by-nd'
  | 'cc-by-nc-nd'
  | 'publisher-specific-oa'
  | 'implied-oa'
  | 'other-oa'
  | 'user_rights_confirmed'
  | 'registry_public'
  | 'unknown';
export type ParseStatus = 'pending' | 'parsed' | 'failed';

export interface DocumentResponse {
  id: string;
  recordId: string | null;
  role: DocumentRole;
  origin: DocumentOrigin;
  title: string;
  sourceUrl: string | null;
  oaVersion: 'publishedVersion' | 'acceptedVersion' | 'submittedVersion' | null;
  mimeType: string | null;
  byteSize: number | null;
  pageCount: number | null;
  sha256: string | null;
  /** The licence as the source reported it (EXTR-032). */
  licence: string | null;
  licenceCode: LicenceCode;
  storagePolicy: StoragePolicy;
  parseStatus: ParseStatus;
  parseError: string | null;
  parser: { name: string; version: string } | null;
  rightsConfirmed: { by: string | null; at: string; textVersion: string } | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface DocumentTextPage {
  /** 1-based. */
  number: number;
  text: string;
}

export interface DocumentTextResponse {
  documentId: string;
  pageCount: number;
  page: DocumentTextPage;
}

/** EXTR-016. */
export type FullTextState = 'pending' | 'retrieved' | 'awaiting_upload' | 'abstract_only' | 'parse_failed' | 'withdrawn';

export type RetrievalAttemptStatus =
  | 'ok'
  | 'not_open_access'
  | 'not_configured'
  | 'no_identifier'
  | 'no_location'
  | 'http_error'
  | 'timeout'
  | 'too_large'
  | 'too_many_redirects'
  | 'insecure_redirect'
  | 'wrong_content'
  | 'parse_failed'
  | 'hash_mismatch';

export interface RetrievalAttemptOut {
  source: 'upload' | 'europepmc' | 'unpaywall' | 'abstract' | 'registry';
  status: RetrievalAttemptStatus;
  /** Plain words for the person reading the list. */
  detail: string | null;
  at: string;
}

/** One record included at title and abstract, with its full text (EXTR-016, EXTR-026). */
export interface FullTextRecordItem {
  recordId: string;
  title: string;
  year: number | null;
  doi: string | null;
  pmid: string | null;
  state: FullTextState;
  /** The full-text screening outcome, when there is one: 'include', 'exclude', or null while undecided. */
  fullTextVerdict: 'include' | 'exclude' | null;
  /** True while the full-text outcome waits for a person (F15 queue). */
  awaitingHuman: boolean;
  documents: DocumentResponse[];
  attempts: RetrievalAttemptOut[];
}

export interface FullTextListResponse {
  items: FullTextRecordItem[];
  counts: Record<FullTextState, number>;
}

/**
 * RunWarning codes of the extract stage: a study's extraction failed (EXTR-059; the study is marked failed and the
 * run goes on). The retrieve and extract stages also use F15's 'ai_unavailable' and 'budget_exhausted'.
 */
export type ExtractionRunWarningCode = 'extraction_failed';

/** EXTR-059. */
export type ExtractionState = 'not_started' | 'queued' | 'running' | 'extracted' | 'failed';

export interface ExtractionQueueItem {
  studyId: string;
  label: string;
  records: Array<{ recordId: string; title: string; fullTextState: FullTextState }>;
  extractionState: ExtractionState;
  /** The AI error kind when extraction failed (EXTR-059). */
  extractionError: string | null;
  counts: { machine: number; confirmed: number; corrected: number; notReported: number; errors: number; warnings: number };
}

export interface ExtractionQueueResponse {
  items: ExtractionQueueItem[];
  nextCursor: string | null;
  /** Machine values of the page waiting for a person (EXTR-075). */
  pendingConfirmations: number;
}

/** 'study' (field 'design', key the study ID) since 14 Oct 2026 (EXTR-215). */
export type ValueEntity = 'study' | 'arm' | 'outcome_result' | 'outcome_data';
/**
 * A value under review. key: the arm id, result id or outcome data id. field: 'role', 'intervention' or
 * 'nRandomised' for arms; 'counts' (events and analysed total together) for results; for outcome data
 * (EXTR-207), 'summary' (mean, SD and n of a continuous result), 'estimate' (a reported estimate with
 * its interval and SE), 'adjustment' (its adjustment variables), 'rate' (events and person-time) or
 * 'correlation' (r and n) (EXTR-215); for the study, 'design' (EXTR-215).
 */
export interface ValueRef {
  studyId: string;
  entity: ValueEntity;
  key: string;
  field: string;
}

/** A continuous result under review (EXTR-207). */
export interface ContinuousSummaryData {
  mean: number;
  sd: number;
  n: number;
}

/** A reported effect estimate under review (EXTR-207); ratios on their natural scale, se on the analysis scale. */
export interface EstimateData {
  measure: 'RR' | 'OR' | 'RD' | 'MD' | 'SMD' | 'HR' | 'IRR';
  estimate: number;
  ciLower: number | null;
  ciUpper: number | null;
  ciLevel: number | null;
  se: number | null;
  adjusted: boolean;
}

/** A rate under review (EXTR-215): events over person-time in person-years. */
export interface RateData {
  events: number;
  personTime: number;
}

/** A correlation under review (EXTR-215). */
export interface CorrelationData {
  r: number;
  n: number;
}

/**
 * The value of a reviewed field. A study design (entity 'study') is a StudyDesign string, or null
 * while it is not recorded on a page with several eligible designs (EXTR-215).
 */
export type ReviewValueData = string | number | null | { events: number; total: number } | ContinuousSummaryData | EstimateData | RateData | CorrelationData;

export interface ConsistencyIssueOut {
  code: string;
  severity: 'error' | 'warning';
  message: string;
  /** The acknowledgement a reviewer gave for this warning, if any (EXTR-084). */
  acknowledged: string | null;
}

export interface ReviewSpan extends Span {
  startOffset: number | null;
  endOffset: number | null;
  locationKind: string;
  /** The cited document's storage policy: spans-only documents show the quote alone (EXTR-095). */
  storagePolicy: StoragePolicy;
}

export interface ReviewValue {
  ref: ValueRef;
  /** Plain label, e.g. "Placebo: events / analysed". */
  label: string;
  /** Where the value sits: the outcome name for results, the arm label for arms. */
  group: string;
  value: ReviewValueData;
  status: ValueStatus;
  version: number;
  /** The main span; for counts, the events span (EXTR-073). Null only for an arm role, which quotes its label's span. */
  span: ReviewSpan | null;
  /** Further sources, e.g. the analysed total quoted elsewhere (EXTR-073). */
  supportingSpans: ReviewSpan[];
  /** 'model:anthropic/<id>', 'user:<id>' or 'system'. Server data; the web app never shows a model name (CONT-058). */
  actor: string;
  relocated: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  /**
   * How a continuous SD or an estimate's SE was derived from printed values (EXTR-202, EXTR-204), with
   * every input's span in `supportingSpans`; absent or null when the value was printed.
   */
  derivation?: Derivation | null;
  /** For results: the timepoint stored, and whether it is the protocol's. */
  timepoint: string | null;
  issues: ConsistencyIssueOut[];
}

export type DropReason =
  | 'foreign_document'
  | 'page_out_of_range'
  | 'quote_not_found'
  | 'quote_ambiguous'
  | 'quote_too_long'
  | 'value_not_in_quote'
  | 'unknown_outcome'
  /** The arm's intervention could not be verified, so the arm and its results are not stored. */
  | 'arm_dropped'
  /** The arm is not part of the protocol's comparison (multi-arm trials); kept in the report only. */
  | 'arm_not_in_comparison'
  /** Events above the analysed total, or a total of zero. */
  | 'impossible_counts'
  /** A second result for the same arm, outcome and timepoint. */
  | 'duplicate_result'
  /** A continuous result without a printed SD, SE or CI of the mean (EXTR-201). */
  | 'no_dispersion'
  /** A reported estimate without a printed SE or CI (EXTR-204). */
  | 'no_precision'
  /** A value that cannot be analysed: a size of 0, a negative SD or SE, a ratio at or below 0, a reversed interval (EXTR-201, EXTR-204). */
  | 'impossible_value'
  /** A data form or estimate measure the outcome's type cannot use (EXTR-212). */
  | 'form_not_for_outcome_type';

export interface DroppedProposal {
  /** Field path in the model output, e.g. "results[2].events". */
  path: string;
  proposed: unknown;
  reason: DropReason;
}

export interface FieldNotReported {
  ref: ValueRef;
  reason: string;
  actor: string;
}

export interface StudyExtractionResponse {
  studyId: string;
  label: string;
  extractionState: ExtractionState;
  extractionError: string | null;
  documents: DocumentResponse[];
  values: ReviewValue[];
  notReported: FieldNotReported[];
  report: { dropped: DroppedProposal[]; modelCallIds: string[]; promptVersion: string | null };
  /** "<reviewed> of <total> values reviewed" (EXTR-097): reviewed is confirmed, corrected or marked not reported. */
  progress: { reviewed: number; total: number };
  /** The next study in the queue with machine values, for the `n` shortcut (EXTR-096). */
  nextStudyId: string | null;
}

export type ValueReviewItem =
  | { action: 'confirm'; ref: ValueRef; expectedVersion: number; acknowledgement?: string }
  | {
      action: 'correct';
      ref: ValueRef;
      expectedVersion: number;
      value: ReviewValueData;
      /** A new source chosen in the source view: offsets into a page of a stored document (EXTR-104). Omitted keeps the current span. */
      newSpan?: { documentId: string; page: number | null; start: number; end: number };
      acknowledgement?: string;
    }
  | { action: 'not_reported'; ref: ValueRef; expectedVersion: number; reason: string }
  | { action: 'reopen'; ref: ValueRef; expectedVersion: number; reason: string };

export interface ValueReviewRequest {
  /** 1 to 200 items (EXTR-100). */
  items: ValueReviewItem[];
}

export interface ValueReviewResponse {
  items: Array<{ ref: ValueRef; status: ValueStatus | 'not_reported'; version: number }>;
  issues: ConsistencyIssueOut[];
  /** EXTR-109: a confirmation run requested after the batch, when one could start. */
  confirmationRunId: string | null;
}

/** details.reason of a 409 conflict from the F16 endpoints (EXTR-146). */
export type ExtractionConflictReason =
  | 'not_stored'
  | 'consistency_error'
  | 'acknowledgement_required'
  | 'version_conflict'
  | 'protocol_not_frozen';
